import { useRef, useState, useCallback } from 'react';
import { useBLSAnimation } from '../hooks/useBLSAnimation';
import { speedToHz } from './SpeedControl';
import type { Direction, MovementStyle, Position, Shape } from '../types';

/**
 * BLSVisual - Animated shape for bilateral stimulation
 *
 * Features:
 * - Smooth 60fps animation
 * - Multiple movement patterns
 * - Multiple shapes (circle, square, triangle, star)
 * - Responsive sizing
 * - GPU-accelerated transforms
 * - Trail effect for "spread" and "pulse" movement styles
 */

interface BLSVisualProps {
  color: string;
  direction: Direction;
  speed: number;
  isPlaying: boolean;
  movementStyle: MovementStyle;
  shape?: Shape; // Shape type (circle, square, triangle, star)
  small?: boolean; // For preview mode
  onPanUpdate?: (panValue: number) => void; // Callback for continuous sound panning
}

/**
 * Darken a hex color by a percentage
 */
function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xFF) + percent));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xFF) + percent));
  const b = Math.max(0, Math.min(255, (num & 0xFF) + percent));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

/**
 * Get CSS classes for different shapes
 */
function getShapeClasses(shape: Shape): string {
  switch (shape) {
    case 'ball':
      return 'rounded-full';
    case 'circle':
      return 'rounded-full';
    case 'square':
      return 'rounded-none';
    case 'triangle':
      return 'rounded-none';
    case 'star':
      return 'rounded-none';
    default:
      return 'rounded-full';
  }
}

/**
 * Get clip-path CSS for shapes that need it
 */
function getShapeClipPath(shape: Shape): string | undefined {
  switch (shape) {
    case 'triangle':
      return 'polygon(50% 0%, 0% 100%, 100% 100%)';
    case 'star':
      return 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)';
    default:
      return undefined;
  }
}

export function BLSVisual({
  color,
  direction,
  speed,
  isPlaying,
  movementStyle,
  shape = 'ball',
  small = false,
  onPanUpdate,
}: BLSVisualProps): JSX.Element {
  const circleRef = useRef<HTMLDivElement>(null);
  const [trailPositions, setTrailPositions] = useState<Position[]>([]);
  const [pulseOpacity, setPulseOpacity] = useState(1);

  // Callback for trail position updates
  const handleTrailUpdate = useCallback((positions: Position[]) => {
    setTrailPositions(positions);
  }, []);

  // Callback to track pan position for audio panning
  const handlePanUpdate = useCallback((pan: number) => {
    onPanUpdate?.(pan);
  }, [onPanUpdate]);

  // Callback for pulse mode opacity
  const handleOpacityUpdate = useCallback((opacity: number) => {
    setPulseOpacity(opacity);
  }, []);

  // Calculate actual circle size in pixels
  const circleSize = small ? 32 : 96; // w-8 = 32px, w-24 = 96px
  const sizeClass = small ? 'w-8 h-8' : 'w-24 h-24';

  // Use animation hook (convert display speed to Hz)
  useBLSAnimation({
    elementRef: circleRef,
    isPlaying,
    direction,
    speed: speedToHz(speed),
    color,
    size: circleSize,
    movementStyle,
    onPanUpdate: handlePanUpdate,
    onTrailUpdate: handleTrailUpdate,
    onOpacityUpdate: handleOpacityUpdate,
  });

  // Calculate effective opacity (pulse mode uses callback-driven opacity, others always 1)
  const effectiveOpacity = movementStyle === 'pulse' ? pulseOpacity : 1;

  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
      {/* Trail shapes for spread mode only (pulse uses opacity-based fade instead) */}
      {movementStyle === 'spread' && trailPositions.slice(1).map((pos, index) => {
        const opacity = 1 - (index + 1) / trailPositions.length; // Linear fade for spread
        const scale = 1 - (index + 1) / (trailPositions.length * 2);
        const clipPath = getShapeClipPath(shape);
        return (
          <div
            key={index}
            className={`absolute ${sizeClass} ${getShapeClasses(shape)}`}
            style={{
              transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px)) scale(${scale})`,
              // Trail balls use softer gradient without harsh white highlight
              background: shape === 'ball'
                ? `radial-gradient(circle at 40% 40%, ${darkenColor(color, 40)} 0%, ${color} 40%, ${darkenColor(color, -50)} 100%)`
                : color,
              opacity: opacity * 0.6,
              left: '50%',
              top: '50%',
              pointerEvents: 'none',
              ...(clipPath ? { clipPath } : {}),
            }}
          />
        );
      })}

      {/* Main shape */}
      <div
        ref={circleRef}
        className={`${sizeClass} ${getShapeClasses(shape)}`}
        style={{
          // 3D ball uses single radial gradient for sphere look - white highlight for realism
          background: shape === 'ball'
            ? `radial-gradient(circle at 30% 30%, #ffffff 0%, ${color} 35%, ${darkenColor(color, -50)} 75%, ${darkenColor(color, -90)} 100%)`
            : color,
          boxShadow: shape === 'ball'
            ? `0 ${small ? '4px' : '8px'} ${small ? '15px' : '30px'} rgba(0,0,0,0.4), 0 0 ${small ? '20px' : '40px'} ${color}50`
            : `0 0 ${small ? '20px' : '40px'} ${color}40`,
          opacity: effectiveOpacity, // Fade effect for pulse mode (always 1 for other modes)
          transform: 'translate(0, 0)', // Initial transform for consistent rendering
          transition: movementStyle === 'pulse' ? 'opacity 0.1s ease-out' : undefined,
          ...(getShapeClipPath(shape) ? { clipPath: getShapeClipPath(shape) } : {}),
        }}
      />
    </div>
  );
}
